import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getAuditReadiness, GetAuditReadinessOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Shield, CheckCircle, AlertTriangle, XCircle, Download, FileText, Lock, Eye } from 'lucide-react';
import { isOwnerOrAdmin } from '@/lib/roles';

type Data = GetAuditReadinessOutputType;
type Check = Data['checks'][0];

const statusConfig = {
  green: { icon: CheckCircle, color: 'text-primary', bg: 'bg-primary/10 border-primary/20', label: 'Pass' },
  amber: { icon: AlertTriangle, color: 'text-accent-foreground', bg: 'bg-accent/10 border-accent/20', label: 'Warning' },
  red: { icon: XCircle, color: 'text-destructive', bg: 'bg-destructive/10 border-destructive/20', label: 'Issue' },
};

function CheckCard({ check }: { check: Check }) {
  const config = statusConfig[check.status];
  const Icon = config.icon;
  return (
    <div className={`border rounded-lg p-3 flex items-start gap-3 ${config.bg}`}>
      <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${config.color}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium text-foreground">{check.label}</p>
          <Badge variant="secondary" className="text-xs">{check.category}</Badge>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{check.detail}</p>
      </div>
    </div>
  );
}

export default function AuditReadiness() {
  const { user } = useAuth();
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !isOwnerOrAdmin(user.role)) { setLoading(false); return; }
    getAuditReadiness({}).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  if (!isOwnerOrAdmin(user?.role)) {
    return (
      <div className="px-4 md:px-8 py-12 text-center">
        <Lock className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-30" />
        <h1 className="text-lg font-semibold text-foreground">Owner / Admin Only</h1>
        <p className="text-sm text-muted-foreground mt-1">The Audit & Readiness Centre is restricted to Owners and Admins.</p>
      </div>
    );
  }

  if (loading) return (
    <div className="px-4 md:px-8 py-6 max-w-5xl mx-auto space-y-4">
      <Skeleton className="h-8 w-64" />
      <div className="grid grid-cols-3 gap-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>
      <div className="space-y-2">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
    </div>
  );

  if (!data) return null;

  const downloadReadiness = () => {
    const lines = ['Staff Hub Readiness Report', `Generated: ${new Date().toISOString().split('T')[0]}`, '', 'Check,Status,Category,Detail'];
    data.checks.forEach(c => {
      lines.push(`"${c.label}","${c.status.toUpperCase()}","${c.category}","${c.detail.replace(/"/g, '""')}"`);
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `readiness-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Readiness report downloaded');
  };

  const categories = [...new Set<string>(data.checks.map((c: Check) => String(c.category)))];

  return (
    <div className="px-4 md:px-8 py-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" /> Audit, Privacy & Readiness Centre
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">System health, safety checks, and compliance readiness</p>
        </div>
        <Button size="sm" variant="outline" onClick={downloadReadiness}>
          <Download className="w-3.5 h-3.5 mr-1.5" /> Export Readiness Pack
        </Button>
      </div>

      {/* RAG Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 text-center">
          <p className="text-3xl font-bold text-primary">{data.summary.green}</p>
          <p className="text-xs text-muted-foreground uppercase tracking-wider mt-1">Pass</p>
        </div>
        <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 text-center">
          <p className="text-3xl font-bold text-accent-foreground">{data.summary.amber}</p>
          <p className="text-xs text-muted-foreground uppercase tracking-wider mt-1">Warning</p>
        </div>
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 text-center">
          <p className="text-3xl font-bold text-destructive">{data.summary.red}</p>
          <p className="text-xs text-muted-foreground uppercase tracking-wider mt-1">Issue</p>
        </div>
      </div>

      {/* Checks by category */}
      {categories.map(cat => (
        <div key={cat}>
          <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{cat}</h2>
          <div className="space-y-2">
            {data.checks.filter(c => c.category === cat).map(c => <CheckCard key={c.id} check={c} />)}
          </div>
        </div>
      ))}

      {/* Audit Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Eye className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold text-foreground">Document Access Logs</h3>
          </div>
          <p className="text-2xl font-bold text-foreground">{data.recentDocAccessLogs}</p>
          <p className="text-xs text-muted-foreground">events recorded</p>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold text-foreground">Export Audit</h3>
          </div>
          <p className="text-2xl font-bold text-foreground">{data.exportAuditCount}</p>
          <p className="text-xs text-muted-foreground">exports logged</p>
        </div>
      </div>

      {/* Recent Audit Logs */}
      {data.recentAuditLogs.length > 0 && (
        <div>
          <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Recent Audit Activity</h2>
          <div className="border border-border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">Action</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">Actor</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">Target</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.recentAuditLogs.slice(0, 15).map(l => (
                  <tr key={l.id} className="hover:bg-muted/30">
                    <td className="px-3 py-2 text-foreground">{l.action}</td>
                    <td className="px-3 py-2 text-muted-foreground">{l.actor}</td>
                    <td className="px-3 py-2 text-muted-foreground">{l.target}</td>
                    <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{l.time ? l.time.split('T')[0] : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
